import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { 
  Flask, ArrowLeft, MagnifyingGlass, Funnel, Flag, SignOut, User
} from '@phosphor-icons/react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { AlertLogSection, FormulationRow } from '../components/AdminPanels';
import { SiteHeader } from '../components/SiteLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function OverrideModal({ open, onOpenChange, formulation, onApply, submitting }) {
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (formulation) {
      setStatus(formulation.overall_status);
      setNotes('');
    }
  }, [formulation]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Override Assessment Status</DialogTitle>
          <DialogDescription>Apply an expert override to {formulation?.metadata.product_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">New Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="override-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="acceptable">Acceptable</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="not_acceptable">Not Acceptable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Expert Commentary</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes explaining the override decision..." rows={4} data-testid="override-notes-input" className="resize-none" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={() => onApply(status, notes)} disabled={submitting || !status} className="btn-primary" data-testid="apply-override-btn">
            {submitting ? 'Applying...' : 'Apply Override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatsGrid({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
      <div className="card-flat p-5 lg:col-span-2 bg-[#0B2447]">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mb-2">Total Assessments</p>
        <p className="text-4xl font-black text-white tabular-nums">{stats.total_formulations}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#03543F] mb-2">Acceptable</p>
        <p className="text-2xl font-black text-[#0E9F6E] tabular-nums">{stats.acceptable_count}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#723B13] mb-2">Needs Review</p>
        <p className="text-2xl font-black text-[#E3A008] tabular-nums">{stats.needs_review_count}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9B1C1C] mb-2">Not Acceptable</p>
        <p className="text-2xl font-black text-[#F05252] tabular-nums">{stats.not_acceptable_count}</p>
      </div>
      <div className="card-flat p-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">Flagged</p>
        <p className="text-2xl font-black text-[#111827] tabular-nums">{stats.flagged_count}</p>
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [formulations, setFormulations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const [overrideModal, setOverrideModal] = useState(false);
  const [selectedFormulation, setSelectedFormulation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [alertLog, setAlertLog] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [inquiriesUnread, setInquiriesUnread] = useState(0);

  const fetchData = useCallback(async () => {
    if (!isAdmin) { navigate('/dashboard'); return; }
    try {
      const [formulationsRes, statsRes, alertsRes, emailCfgRes, inquiriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/formulations`, {
          params: { status: statusFilter !== 'all' ? statusFilter : undefined, flagged_only: flaggedOnly },
          withCredentials: true
        }),
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/alerts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/email-config`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/inquiries/unread-count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
      ]);
      setFormulations(formulationsRes.data);
      setStats(statsRes.data);
      setAlertLog(alertsRes.data);
      setEmailConfig(emailCfgRes.data);
      setInquiriesUnread(inquiriesRes.data?.count || 0);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, navigate, statusFilter, flaggedOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleToggleFlag = async (formulationId) => {
    try {
      await axios.post(`${API_URL}/api/admin/formulations/${formulationId}/flag`, {}, { withCredentials: true });
      fetchData();
    } catch (error) { console.error('Error toggling flag:', error); }
  };

  const openOverrideModal = (formulation) => {
    setSelectedFormulation(formulation);
    setOverrideModal(true);
  };

  const handleOverride = async (status, notes) => {
    if (!selectedFormulation || !status) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/formulations/${selectedFormulation.id}/override`, { status, notes }, { withCredentials: true });
      setOverrideModal(false);
      fetchData();
    } catch (error) { console.error('Error applying override:', error); }
    finally { setSubmitting(false); }
  };

  const filteredFormulations = formulations.filter(f => {
    const q = searchQuery.toLowerCase();
    const u = f.user_info || {};
    return (
      (f.metadata?.product_name || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.company || '').toLowerCase().includes(q)
    );
  });

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold tracking-tight text-[#111827]">Admin Dashboard</h1>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9B1C1C] bg-[#FDE8E8] px-2 py-0.5 rounded">Admin</span>
          </div>
          <p className="text-[#4B5563] mt-1">Agyekum Group oversight and intervention panel</p>
        </div>

        {/* Inquiries entry card with unread badge */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/admin/inquiries"
            data-testid="admin-inquiries-card"
            className="flex items-center justify-between gap-4 p-5 rounded-sm bg-white border border-gray-200 hover:border-[#0B1426] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#FBF8F1] border border-gray-200 flex items-center justify-center text-[#E63946] text-lg">✉</div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5B6577]">Partnership Inquiries</p>
                <p className="text-base font-semibold text-[#0B1426] mt-0.5">
                  View, triage, and respond to inbound enterprise inquiries
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {inquiriesUnread > 0 && (
                <span
                  className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-[11px] font-bold tabular-nums text-white"
                  style={{ backgroundColor: '#E63946' }}
                  data-testid="admin-inquiries-unread-badge"
                  aria-label={`${inquiriesUnread} new inquiries`}
                >
                  {inquiriesUnread}
                </span>
              )}
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#5B6577]">Open →</span>
            </div>
          </Link>
          <Link
            to="/admin/ops"
            data-testid="admin-ops-card"
            className="flex items-center justify-between gap-4 p-5 rounded-sm bg-white border border-gray-200 hover:border-[#0B1426] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#FBF8F1] border border-gray-200 flex items-center justify-center text-[#E63946] text-lg">◉</div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#5B6577]">Mission Control</p>
                <p className="text-base font-semibold text-[#0B1426] mt-0.5">
                  Workflow watchdog · AI quality · system health
                </p>
              </div>
            </div>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#5B6577]">Open →</span>
          </Link>
        </div>

        <StatsGrid stats={stats} />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input type="text" placeholder="Search by product, user name, or email..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="admin-search-input" className="w-full h-11 pl-12 pr-4 rounded-sm border border-gray-200 bg-white focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none" />
          </div>
          <div className="flex gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] h-11 rounded-sm" data-testid="status-filter"><Funnel size={16} className="mr-2 text-[#4B5563]" /><SelectValue placeholder="Filter by status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="acceptable">Acceptable</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="not_acceptable">Not Acceptable</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={flaggedOnly ? "default" : "outline"} onClick={() => setFlaggedOnly(!flaggedOnly)} className={`h-11 rounded-sm ${flaggedOnly ? 'bg-[#F05252] hover:bg-[#DC2626]' : ''}`} data-testid="flagged-filter-btn">
              <Flag size={18} className="mr-2" /> Flagged Only
            </Button>
          </div>
        </div>

        {/* Formulations List */}
        <div className="card-flat overflow-hidden">
          <div className="p-4 border-b border-gray-200"><h2 className="text-lg font-semibold text-[#111827]">All Submissions</h2></div>
          {loading ? (
            <div className="p-8">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={`skel-${i}`} className="flex items-center gap-4 mb-4">
                  <div className="skeleton w-10 h-10 rounded-sm" /><div className="flex-1"><div className="skeleton h-4 w-48 mb-2" /><div className="skeleton h-3 w-32" /></div><div className="skeleton h-6 w-24 rounded-sm" />
                </div>
              ))}
            </div>
          ) : filteredFormulations.length === 0 ? (
            <div className="p-12 text-center"><p className="text-[#4B5563]">No formulations found</p></div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredFormulations.map((formulation) => (
                <FormulationRow key={formulation.id} formulation={formulation} onOverride={openOverrideModal} onToggleFlag={handleToggleFlag} />
              ))}
            </div>
          )}
        </div>

        <AlertLogSection alertLog={alertLog} emailConfig={emailConfig} />
      </main>

      <OverrideModal open={overrideModal} onOpenChange={setOverrideModal} formulation={selectedFormulation} onApply={handleOverride} submitting={submitting} />
    </div>
  );
}
